/**
 * CityPicker — modern cascading Region → Zone → Woreda → City selector.
 *
 * UX:
 * - Region / Zone / Woreda shown as a modern card-style stepper with icons
 * - Each step shows a badge when selected, grayed out when locked
 * - City is a custom dropdown with an inline search field at the top
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { loadAllLocations } from "../../services/locationService";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

/* ─── Icons ─────────────────────────────────────────────────────────────────── */

const ChevronDown = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = ({ size = 10 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/* ─── FilterDropdown — shared dropdown used for Region, Zone, Woreda ─────────── */

const FilterDropdown = ({
  label,
  value,
  options,
  onChange,
  onClear,
  disabled,
  stepNumber,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search]);

  const selectedName = value ? options.find((o) => o.id === value)?.name : null;
  const isSelected = !!selectedName;

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onClear();
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={ref} style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "9px 12px",
          borderRadius: "10px",
          border: isSelected
            ? "1.5px solid var(--color-primary, #3b82f6)"
            : open
              ? "1.5px solid var(--color-primary, #3b82f6)"
              : "1.5px solid var(--border-primary, #d1d5db)",
          background: isSelected
            ? "rgba(59,130,246,0.06)"
            : disabled
              ? "var(--bg-secondary, #f3f4f6)"
              : "var(--bg-primary, #fff)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
          boxShadow: open ? "0 0 0 3px rgba(59,130,246,0.12)" : "none",
          textAlign: "left",
        }}
      >
        {/* Step number badge */}
        <span
          style={{
            flexShrink: 0,
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: isSelected
              ? "var(--color-primary, #3b82f6)"
              : disabled
                ? "var(--border-primary, #d1d5db)"
                : "var(--bg-secondary, #e5e7eb)",
            color: isSelected ? "#fff" : "var(--text-secondary, #9ca3af)",
            fontSize: "0.6875rem",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected ? <CheckIcon /> : stepNumber}
        </span>

        {/* Label / selected value */}
        <span
          style={{
            flex: 1,
            fontSize: "0.8125rem",
            fontWeight: isSelected ? "600" : "400",
            color: isSelected
              ? "var(--color-primary, #3b82f6)"
              : disabled
                ? "var(--text-secondary, #9ca3af)"
                : "var(--text-primary, #374151)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedName || label}
        </span>

        {/* Clear + chevron */}
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            flexShrink: 0,
          }}
        >
          {isSelected && (
            <span
              onClick={handleClear}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                background: "rgba(59,130,246,0.15)",
                cursor: "pointer",
              }}
            >
              <XIcon size={9} />
            </span>
          )}
          <span
            style={{
              opacity: 0.4,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
              display: "flex",
            }}
          >
            <ChevronDown />
          </span>
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 1000,
            background: "var(--bg-primary, #fff)",
            border: "1px solid var(--border-primary, #e5e7eb)",
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.13)",
            minWidth: "220px",
            width: "max-content",
            maxWidth: "300px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "8px 8px 6px",
              borderBottom: "1px solid var(--border-primary, #f0f0f0)",
            }}
          >
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: "9px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.4,
                  display: "flex",
                }}
              >
                <SearchIcon />
              </span>
              <input
                autoFocus
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 10px 6px 28px",
                  borderRadius: "7px",
                  border: "1px solid var(--border-primary, #d1d5db)",
                  background: "var(--bg-secondary, #f9fafb)",
                  fontSize: "0.8125rem",
                  outline: "none",
                  color: "var(--text-primary, #111)",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div style={{ maxHeight: "210px", overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "12px 14px",
                  fontSize: "0.8125rem",
                  color: "var(--text-secondary, #9ca3af)",
                  textAlign: "center",
                }}
              >
                No results
              </div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => handleSelect(o.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 14px",
                    background:
                      value === o.id ? "rgba(59,130,246,0.08)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.8125rem",
                    color:
                      value === o.id
                        ? "var(--color-primary, #3b82f6)"
                        : "var(--text-primary, #111)",
                    fontWeight: value === o.id ? "600" : "400",
                    textAlign: "left",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (value !== o.id)
                      e.currentTarget.style.background =
                        "var(--bg-secondary, #f3f4f6)";
                  }}
                  onMouseLeave={(e) => {
                    if (value !== o.id)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {o.name}
                  {value === o.id && <CheckIcon />}
                </button>
              ))
            )}
          </div>

          {filtered.length > 0 && (
            <div
              style={{
                padding: "5px 12px",
                fontSize: "0.7rem",
                color: "var(--text-secondary, #9ca3af)",
                borderTop: "1px solid var(--border-primary, #f0f0f0)",
              }}
            >
              {filtered.length} {filtered.length === 1 ? "result" : "results"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── CityDropdown — the main city selector with inline search ─────────────── */

const CityDropdown = ({ cities, value, onChange, disabled, error }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return cities;
    const q = search.toLowerCase();
    return cities.filter((c) => c.name.toLowerCase().includes(q));
  }, [cities, search]);

  const selectedName = value ? cities.find((c) => c.id === value)?.name : null;

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          padding: "11px 14px",
          borderRadius: "10px",
          border: error
            ? "1.5px solid var(--color-danger, #ef4444)"
            : open
              ? "1.5px solid var(--color-primary, #3b82f6)"
              : "1.5px solid var(--border-primary, #d1d5db)",
          background: "var(--bg-primary, #fff)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          fontSize: "0.9375rem",
          color: selectedName
            ? "var(--text-primary, #111)"
            : "var(--text-secondary, #9ca3af)",
          textAlign: "left",
          boxShadow: open ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        <span
          style={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedName ||
            (disabled ? "Select filters above first" : "Select a city")}
        </span>
        <span
          style={{
            flexShrink: 0,
            opacity: 0.4,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
            display: "flex",
          }}
        >
          <ChevronDown />
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 999,
            background: "var(--bg-primary, #fff)",
            border: "1px solid var(--border-primary, #e5e7eb)",
            borderRadius: "12px",
            boxShadow: "0 12px 36px rgba(0,0,0,0.14)",
            overflow: "hidden",
          }}
        >
          {/* Sticky search */}
          <div
            style={{
              padding: "8px 8px 6px",
              borderBottom: "1px solid var(--border-primary, #f0f0f0)",
              background: "var(--bg-primary, #fff)",
              position: "sticky",
              top: 0,
            }}
          >
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.4,
                  display: "flex",
                }}
              >
                <SearchIcon />
              </span>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px 8px 30px",
                  borderRadius: "7px",
                  border: "1px solid var(--border-primary, #d1d5db)",
                  background: "var(--bg-secondary, #f9fafb)",
                  fontSize: "0.875rem",
                  outline: "none",
                  color: "var(--text-primary, #111)",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div style={{ maxHeight: "230px", overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "16px",
                  fontSize: "0.875rem",
                  color: "var(--text-secondary, #9ca3af)",
                  textAlign: "center",
                }}
              >
                No cities found
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleSelect(c.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "9px 14px",
                    background:
                      value === c.id ? "rgba(59,130,246,0.08)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    color:
                      value === c.id
                        ? "var(--color-primary, #3b82f6)"
                        : "var(--text-primary, #111)",
                    fontWeight: value === c.id ? "600" : "400",
                    textAlign: "left",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (value !== c.id)
                      e.currentTarget.style.background =
                        "var(--bg-secondary, #f3f4f6)";
                  }}
                  onMouseLeave={(e) => {
                    if (value !== c.id)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {c.name}
                  {value === c.id && <CheckIcon />}
                </button>
              ))
            )}
          </div>

          {filtered.length > 0 && (
            <div
              style={{
                padding: "5px 12px",
                fontSize: "0.75rem",
                color: "var(--text-secondary, #9ca3af)",
                borderTop: "1px solid var(--border-primary, #f0f0f0)",
              }}
            >
              {filtered.length} {filtered.length === 1 ? "city" : "cities"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Main CityPicker ───────────────────────────────────────────────────────── */

const CityPicker = ({
  label = "City",
  value = "",
  onChange,
  required = false,
  disabled = false,
  error = null,
}) => {
  const [locations, setLocations] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedZone, setSelectedZone] = useState("");
  const [selectedWoreda, setSelectedWoreda] = useState("");

  useEffect(() => {
    loadAllLocations()
      .then(setLocations)
      .catch((e) => setLoadError(e.message));
  }, []);

  const filteredZones = useMemo(() => {
    if (!locations) return [];
    return locations.zones.filter(
      (z) => !selectedRegion || z.regionId === selectedRegion,
    );
  }, [locations, selectedRegion]);

  const filteredWoredas = useMemo(() => {
    if (!locations) return [];
    return locations.woredas.filter(
      (w) => !selectedZone || w.zoneId === selectedZone,
    );
  }, [locations, selectedZone]);

  const filteredCities = useMemo(() => {
    if (!locations) return [];
    let cities = locations.cities;
    if (selectedWoreda) {
      cities = cities.filter((c) => c.woredaId === selectedWoreda);
    } else if (selectedZone) {
      const woredaIds = new Set(
        locations.woredas
          .filter((w) => w.zoneId === selectedZone)
          .map((w) => w.id),
      );
      cities = cities.filter((c) => woredaIds.has(c.woredaId));
    } else if (selectedRegion) {
      const zoneIds = new Set(
        locations.zones
          .filter((z) => z.regionId === selectedRegion)
          .map((z) => z.id),
      );
      const woredaIds = new Set(
        locations.woredas.filter((w) => zoneIds.has(w.zoneId)).map((w) => w.id),
      );
      cities = cities.filter((c) => woredaIds.has(c.woredaId));
    }
    return cities;
  }, [locations, selectedRegion, selectedZone, selectedWoreda]);

  const handleRegionChange = (id) => {
    setSelectedRegion(id);
    setSelectedZone("");
    setSelectedWoreda("");
    if (onChange && value) onChange("");
  };

  const handleZoneChange = (id) => {
    setSelectedZone(id);
    setSelectedWoreda("");
    if (onChange && value) onChange("");
  };

  const handleWoredaChange = (id) => {
    setSelectedWoreda(id);
    if (onChange && value) onChange("");
  };

  if (loadError)
    return <ErrorMessage message={`Failed to load cities: ${loadError}`} />;

  if (!locations) {
    return (
      <div className="form-field">
        <label className="form-label">{label}</label>
        <LoadingSpinner size="small" message="Loading cities..." />
      </div>
    );
  }

  const activeStep = !selectedRegion
    ? 0
    : !selectedZone
      ? 1
      : !selectedWoreda
        ? 2
        : 3;

  return (
    <div className="form-field city-picker">
      <label className="form-label">
        {label}
        {required && (
          <span className="required-indicator" aria-label="required">
            *
          </span>
        )}
      </label>

      {/* ── Modern filter card ── */}
      <div
        style={{
          background: "var(--bg-secondary, #f9fafb)",
          border: "1px solid var(--border-primary, #e5e7eb)",
          borderRadius: "12px",
          padding: "12px",
          marginBottom: "10px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginBottom: "10px",
          }}
        >
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: "600",
              color: "var(--text-secondary, #6b7280)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Filter by location
          </span>
          {(selectedRegion || selectedZone || selectedWoreda) && (
            <button
              type="button"
              onClick={() => {
                handleRegionChange("");
              }}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "0.75rem",
                color: "var(--color-primary, #3b82f6)",
                padding: "2px 6px",
                borderRadius: "4px",
                fontWeight: "500",
              }}
            >
              Clear all
            </button>
          )}
        </div>

        {/* Progress connector + steps */}
        <div style={{ display: "flex", gap: "6px", alignItems: "stretch" }}>
          <FilterDropdown
            label="Region"
            value={selectedRegion}
            options={locations.regions}
            onChange={handleRegionChange}
            onClear={() => handleRegionChange("")}
            disabled={disabled}
            stepNumber="1"
          />

          {/* Connector */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              opacity: selectedRegion ? 1 : 0.25,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>

          <FilterDropdown
            label="Zone"
            value={selectedZone}
            options={filteredZones}
            onChange={handleZoneChange}
            onClear={() => handleZoneChange("")}
            disabled={disabled || !selectedRegion}
            stepNumber="2"
          />

          {/* Connector */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              opacity: selectedZone ? 1 : 0.25,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>

          <FilterDropdown
            label="Woreda"
            value={selectedWoreda}
            options={filteredWoredas}
            onChange={handleWoredaChange}
            onClear={() => handleWoredaChange("")}
            disabled={disabled || !selectedZone}
            stepNumber="3"
          />
        </div>

        {/* Active step hint */}
        {activeStep < 3 && (
          <p
            style={{
              margin: "8px 0 0",
              fontSize: "0.75rem",
              color: "var(--text-secondary, #9ca3af)",
            }}
          >
            {activeStep === 0 &&
              "Start by selecting a region to narrow down cities"}
            {activeStep === 1 && "Now select a zone within the region"}
            {activeStep === 2 &&
              "Finally select a woreda, then pick your city below"}
          </p>
        )}
      </div>

      {/* ── City dropdown with inline search ── */}
      <CityDropdown
        cities={filteredCities}
        value={value}
        onChange={(id) => onChange && onChange(id)}
        disabled={disabled}
        error={error}
      />

      <ErrorMessage message={error} className="field-error" />
    </div>
  );
};

export default CityPicker;
